import { useLogisticaConfiguration, withLogisticaConfiguration } from "../logistica/configurazione/LogisticaConfigurationProvider";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import DataTable from "../interfaccia/DataTableBase";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import OrdineVoceCliccabile from "../logistica/AreaOrdiniComponenti/DBperAreaOrdiniComponenti/OrdineVoceCliccabile";
import ModificaOrdini from "../logistica/AreaOrdiniComponenti/ModificaOrdini";
import ModalRicercaTransazioneDomino from "./ModalRicercaTransazioneDomino";

const REQUIRED_COLUMNS = [
  "TRANSACTION_TYPE",
  "TRANSACTION_EVENT_ID",
  "TOTAL_ACTIVITY_VALUE_AMT_VAT_INCL",
];

function parseCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  cells.push(current);
  return cells;
}

function normalizeAmazonDate(value) {
  const date = String(value || "").trim();
  const match = date.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return match ? match[3] + "-" + match[2] + "-" + match[1] : date;
}

function parseAmazonCsv(text) {
  const normalizedText = text
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  const lines = normalizedText.split("\n").filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    throw new Error("Il CSV non contiene righe dati");
  }

  const headers = parseCsvLine(lines[0]).map((header) => header.trim());
  const missingColumns = REQUIRED_COLUMNS.filter(
    (column) => !headers.includes(column),
  );
  if (missingColumns.length > 0) {
    throw new Error(`Colonne mancanti: ${missingColumns.join(", ")}`);
  }

  return lines.slice(1).map((line, index) => {
    const cells = parseCsvLine(line);
    const row = headers.reduce((acc, header, cellIndex) => {
      acc[header] = cells[cellIndex] || "";
      return acc;
    }, {});
    const arrivalCountry = String(row.ARRIVAL_COUNTRY || "").trim().toUpperCase();
    const saleArrivalCountry = String(row.SALE_ARRIVAL_COUNTRY || "").trim().toUpperCase();
    const country = arrivalCountry || saleArrivalCountry;

    return {
      sourceIndex: index,
      csvRowNumber: index + 2,
      transactionType: row.TRANSACTION_TYPE,
      transactionEventId: row.TRANSACTION_EVENT_ID,
      activityTransactionId: row.ACTIVITY_TRANSACTION_ID,
      amount: row.TOTAL_ACTIVITY_VALUE_AMT_VAT_INCL,
      currency: row.TRANSACTION_CURRENCY_CODE,
      activityPeriod: row.ACTIVITY_PERIOD,
      marketplace: row.MARKETPLACE,
      arrivalCountry,
      saleArrivalCountry,
      country,
      countryCode: country,
      salesChannel: row.SALES_CHANNEL,
      transactionCompleteDate: normalizeAmazonDate(
        row.TRANSACTION_COMPLETE_DATE,
      ),
      transactionDepartDate: normalizeAmazonDate(row.TRANSACTION_DEPART_DATE),
      sellerSku: row.SELLER_SKU,
      asin: row.ASIN,
      itemDescription: row.ITEM_DESCRIPTION,
    };
  });
}

function formatImporto(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return value;
  }
  return number.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatData(value) {
  if (!value) {
    return "";
  }
  return String(value).slice(0, 10);
}

function toInputDate(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
}

async function calcolaHashFile(file) {
  if (!window.crypto || !window.crypto.subtle) {
    return null;
  }
  const buffer = await file.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);
  const bytes = Array.from(new Uint8Array(hashBuffer));
  return bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function getPeriodoDaRows(rows) {
  const dateValues = rows
    .map((row) =>
      toInputDate(row.transactionCompleteDate || row.transactionDepartDate),
    )
    .filter(Boolean)
    .sort();

  if (dateValues.length < 1) {
    return { periodoDa: "", periodoA: "" };
  }

  return {
    periodoDa: dateValues[0],
    periodoA: dateValues[dateValues.length - 1],
  };
}

function parseMaybeJson(value, fallback) {
  if (!value) {
    return fallback;
  }
  if (typeof value === "object") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function buildAbbinamentiConferma(row) {
  if (row.transactionType === "SALE") {
    return (row.matchedShipmentIds || []).map((idSpedizione) => {
      const spedizione = (row.spedizioni || []).find(
        (item) => String(item.idSpedizioneDomino) === String(idSpedizione),
      );

      return {
        tipoEntita: "SPEDIZIONE",
        idSpedizione,
        importoDomino: spedizione ? spedizione.totaleImportoSpedizione : null,
      };
    });
  }

  if (row.transactionType === "REFUND" && row.matchedResoId) {
    const reso = (row.resi || []).find(
      (item) => String(item.idReso) === String(row.matchedResoId),
    );

    return [
      {
        tipoEntita: "RESO",
        idReso: row.matchedResoId,
        importoDomino: reso ? reso.totaleRimborso : null,
      },
    ];
  }

  return [];
}

function haAbbinamentoDominoCorretto(row) {
  const abbinamenti = buildAbbinamentiConferma(row);
  if (abbinamenti.length < 1) {
    return false;
  }

  const importoReport = Math.abs(Number(row.amount || 0));
  const importoDomino = abbinamenti.reduce(
    (totale, abbinamento) =>
      totale + Math.abs(Number(abbinamento.importoDomino || 0)),
    0,
  );

  return Math.abs(importoReport - importoDomino) < 0.005;
}

function StatoBadge({ row }) {
  if (row.statoControllo === "MANUALE") {
    return (
      <div className="tastoArancio ba br3 w2 h2 flex items-center justify-center">
        <span className="material-symbols-outlined">visibility</span>
      </div>
    );
  }

  const ok = row.statoControllo === "OK";

  if (ok) {
    return (
      <div className="tastoVerde ba br3 w2 h2 flex items-center justify-center white">
        <span className="material-symbols-outlined">check</span>
      </div>
    );
  } else {
    return (
      <div className="tastoRosso ba br3 w2 h2 flex items-center justify-center white">
        <span className="material-symbols-outlined">dangerous</span>
      </div>
    );
  }
}

function TipoTransazione({ row }) {
  if (row.transactionType === "REFUND") {
    return <span className="red">RIMBORSO</span>;
  }

  if (row.transactionType === "SALE") {
    return <span className="green">VENDITA</span>;
  }

  return <span className="">{row.transactionType}</span>;
}

function DettaglioRigheReportCell({ row }) {
  const righeReport = Array.isArray(row.righeReport) ? row.righeReport : [];
  if (righeReport.length < 1) {
    return <span className="colScrittaDef-20">nessun dettaglio</span>;
  }

  return (
    <div className="flex flex-column">
      {righeReport.slice(0, 4).map((riga) => (
        <div key={riga.csvRowNumber} className="mb1">
          <span className="b">#{riga.csvRowNumber}</span> {riga.sellerSku}{" "}
          {formatImporto(riga.amount)}
          <div className="f7">{riga.itemDescription}</div>
        </div>
      ))}
      {righeReport.length > 4 && (
        <div className="f7 b">+{righeReport.length - 4} righe</div>
      )}
    </div>
  );
}

function SpedizioniCell({ row, setOrdine }) {
  if (!row.spedizioni || row.spedizioni.length < 1) {
    return <span className="colScrittaDef-20">nessuna</span>;
  }

  return (
    <div className="flex flex-column">
      {row.spedizioni.map((spedizione) => (
        <div key={spedizione.idSpedizioneDomino} className="mb1">
          <OrdineVoceCliccabile
            setOrdineFunction={setOrdine}
            idSped={spedizione.idSpedizioneDomino}
            datoDisplay={
              <div className="flex items-center">
                {stilePulsanti.tastoApriAspetto}
                <span className="b ml2">{spedizione.idSpedizioneDomino}</span>
                <span className="ml2">{spedizione.numeroOrdinePrestashop}</span>
              </div>
            }
          />
          <div className="f7">
            {spedizione.statoOrdine} -{" "}
            {formatImporto(spedizione.totaleImportoSpedizione)}
          </div>
        </div>
      ))}
    </div>
  );
}

function ValoriSpedizioniCell({ row, fieldName }) {
  const values = Array.from(
    new Set(
      (row.spedizioni || [])
        .map((spedizione) => spedizione[fieldName])
        .filter(
          (value) =>
            value !== null &&
            value !== undefined &&
            String(value).trim() !== "",
        ),
    ),
  );

  if (values.length < 1) {
    return <span className="colScrittaDef-20">-</span>;
  }

  return (
    <div className="flex flex-column">
      {values.map((value) => (
        <span key={value}>{value}</span>
      ))}
    </div>
  );
}

function ResiCell({ row }) {
  if (!row.resi || row.resi.length < 1) {
    return <span className="colScrittaDef-20">nessuno</span>;
  }

  return (
    <div className="flex flex-column">
      {row.resi.map((reso) => (
        <div
          key={reso.idReso}
          className={row.matchedResoId === reso.idReso ? "b" : ""}
        >
          #{reso.idReso} {formatImporto(reso.totaleRimborso)} {reso.statoReso}
        </div>
      ))}
    </div>
  );
}

function getControlliCorrispettiviEsistenti(row) {
  const controlli = [];

  (row.spedizioni || []).forEach((spedizione) => {
    (spedizione.controlliCorrispettiviEsistenti || []).forEach((controllo) => {
      controlli.push({
        ...controllo,
        tipoEntita: "SPEDIZIONE",
        idEntita: spedizione.idSpedizioneDomino,
      });
    });
  });

  (row.resi || []).forEach((reso) => {
    (reso.controlliCorrispettiviEsistenti || []).forEach((controllo) => {
      controlli.push({
        ...controllo,
        tipoEntita: "RESO",
        idEntita: reso.idReso,
      });
    });
  });

  return controlli;
}

function ControlliPrecedentiCell({ row }) {
  const controlli = getControlliCorrispettiviEsistenti(row);

  if (controlli.length < 1) {
    return <span className="colScrittaDef-20">-</span>;
  }

  return (
    <div className="flex flex-column">
      {controlli.map((controllo) => (
        <div
          key={
            String(controllo.tipoEntita) +
            "-" +
            String(controllo.idEntita) +
            "-" +
            String(
              controllo.idAbbinamentoControllo || controllo.idRigaControllo,
            )
          }
          className="tastoArancio br2 ph2 pv1 mb1"
        >
          <div className="b">
            {controllo.tipoEntita} #{controllo.idEntita}
          </div>
          <div>{controllo.nomeFileControllo || "File controllo"}</div>
          <div className="f7">
            riga{" "}
            {controllo.numeroRigaControllo ||
              controllo.progressivoRigaControllo ||
              "-"}
            {" - "}
            {formatData(controllo.registeredAt || controllo.confirmedAt)}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProblemiCell({ row }) {
  const controlliManuali = Array.isArray(row.controlliManuali)
    ? row.controlliManuali
    : [];
  if (
    (!row.problemi || row.problemi.length < 1) &&
    controlliManuali.length < 1
  ) {
    return <span className="tastoVerde white br2 ph2 pv1">coerente</span>;
  }

  return (
    <div className="flex flex-column">
      {controlliManuali.map((controllo) => (
        <span
          key={controllo}
          className="tastoArancio br2 ph2 pv1 mb1 flex items-center"
        >
          <span className="material-symbols-outlined mr1">visibility</span>
          {controllo}
        </span>
      ))}
      {row.problemi.map((problema) => (
        <span key={problema} className="tastoArancio br2 ph2 pv1 mb1">
          {problema}
        </span>
      ))}
    </div>
  );
}

function getStatoRegistrazione(row) {
  return (
    row.statoRegistrazione ||
    row.stato_registrazione ||
    (row.confermata ? "CONFERMATO" : "IN_ATTESA_CONFERMA")
  );
}

function StatoRegistrazioneBadge({ row }) {
  const stato = getStatoRegistrazione(row);
  if (stato === "CONFERMATO")
    return (
      <div
        className="tastoVerde white br3 w2 h2 flex items-center justify-center"
        title="Confermato"
      >
        <span className="material-symbols-outlined">check</span>
      </div>
    );
  if (stato === "DA_IGNORARE")
    return (
      <div
        className="sfondoColScuro white br3 w2 h2 flex items-center justify-center"
        title="Ignorato"
      >
        <span className="material-symbols-outlined">block</span>
      </div>
    );
  return null;
}

function isRigaSelezionabile(row) {
  return Boolean(row.idRigaControllo);
}

function isRigaConfermabile(row) {
  return (
    isRigaSelezionabile(row) &&
    getStatoRegistrazione(row) === "IN_ATTESA_CONFERMA"
  );
}

function ConfermaCell({ row, isLoading, onConferma, onDeconferma, onIgnora }) {
  if (!row.idRigaControllo) {
    return <span className="colScrittaDef-20">salva file</span>;
  }

  const stato = getStatoRegistrazione(row);
  return (
    <div className="flex items-center">
      {/* <StatoRegistrazioneBadge row={row} /> */}
      <div className="flex items-center ml2">
        {stato === "IN_ATTESA_CONFERMA" ? (
          <>
            <button
              className="tastoVerde white br2 ph2 pv1 mr1 pointer"
              type="button"
              title="Conferma"
              aria-label="Conferma"
              disabled={isLoading}
              onClick={() => onConferma(row)}
            >
              <span className="material-symbols-outlined">thumb_up</span>
            </button>
            <button
              className="sfondoColScuro white br2 ph2 pv1 pointer"
              type="button"
              title="Ignora"
              aria-label="Ignora"
              disabled={isLoading}
              onClick={() => onIgnora(row)}
            >
              <span className="material-symbols-outlined">thumb_down</span>
            </button>
          </>
        ) : (
          <button
            className="tastoRosso white br2 ph2 pv1 pointer"
            type="button"
            title="Riporta in attesa"
            aria-label="Riporta in attesa"
            disabled={isLoading}
            onClick={() => onDeconferma(row)}
          >
            <span className="material-symbols-outlined">undo</span>
          </button>
        )}
      </div>
    </div>
  );
}

function ModalCambioValuta({
  riga,
  valore,
  onChange,
  onClose,
  onSave,
  saving,
}) {
  if (!riga) return null;
  return (
    <div className="modal" onClick={onClose}>
      <div
        id="modal-content"
        className="sfondoBiancoTema pa3 w-90 w-40-l"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-between items-center mb3">
          <div className="f4 b">Tasso di cambio</div>
          <button
            className="dim tastoInputOrizzontale br3"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            CHIUDI
          </button>
        </div>
        <div className="mb3">
          EUR 1 = {riga.valutaEstera} il{" "}
          {String(riga.dataCambio).split("-").reverse().join("/")}
        </div>
        <input
          className="inputOrizzontale w-100 mb3"
          type="text"
          inputMode="decimal"
          autoFocus
          placeholder="Es. 11,0935"
          value={valore}
          onChange={(event) => onChange(event.target.value)}
        />
        <div className="flex justify-end">
          <button
            className="tastoVerde white br2 ph2 pv1 flex justify-center items-center"
            type="button"
            onClick={onSave}
            disabled={saving || !String(valore).trim()}
          >
            <span className="material-symbols-outlined mr1">save</span>
            {saving ? "SALVO" : "SALVA"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalImportoRiepilogo({ valore, onChange, onClose, onSave, saving }) {
  return <div className="modal" onClick={onClose}><div id="modal-content" className="sfondoBiancoTema pa3 w-90 w-40-l" onClick={(event) => event.stopPropagation()}><div className="flex justify-between items-center mb3"><div className="f4 b">Conferma con importo differente</div><button className="dim tastoInputOrizzontale br3" type="button" onClick={onClose} disabled={saving}>CHIUDI</button></div><div className="mb2 colScrittaDef-20">Importo da conteggiare nel riepilogo confermati</div><input className="inputOrizzontale w-100 mb3" type="text" inputMode="decimal" value={valore} onChange={(event) => onChange(event.target.value)} /><div className="flex justify-end"><button className="tastoArancio br2 ph2 pv1 flex justify-center items-center" type="button" onClick={onSave} disabled={saving || !String(valore).trim()}><span className="material-symbols-outlined mr1">swap_vert</span>{saving ? "CONFERMO" : "CONFERMA"}</button></div></div></div>;
}

function ControlloCorrispettiviAmazon({ token, utente }) {
  const { datiTendineOrdine } = useLogisticaConfiguration();

  const [searchParams] = useSearchParams();
  const fileId = searchParams.get("fileId");
  const [fileName, setFileName] = useState("");
  const [fileHash, setFileHash] = useState(null);
  const [accountControllo, setAccountControllo] = useState("");
  const [periodoDa, setPeriodoDa] = useState("");
  const [periodoA, setPeriodoA] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState("");
  const [ordine, setOrdine] = useState("NO DATA");
  const [savedFileId, setSavedFileId] = useState(null);
  const [savingFile, setSavingFile] = useState(false);
  const [salvataggioErrore, setSalvataggioErrore] = useState("");
  const [confermaErrore, setConfermaErrore] = useState("");
  const [confermaMessaggio, setConfermaMessaggio] = useState("");
  const [righeInConferma, setRigheInConferma] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkUnconfirming, setBulkUnconfirming] = useState(false);
  const [bulkIgnoring, setBulkIgnoring] = useState(false);
  const [toggleClearSelectedRows, setToggleClearSelectedRows] = useState(false);
  const [filtroRifAmazon, setFiltroRifAmazon] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEsito, setFiltroEsito] = useState("");
  const [filtroConferma, setFiltroConferma] = useState("");
  const [rigaDaCollegare, setRigaDaCollegare] = useState(null);
  const [rigaCambio, setRigaCambio] = useState(null);
  const [valoreCambio, setValoreCambio] = useState("");
  const [savingCambio, setSavingCambio] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [rigaImportoRiepilogo, setRigaImportoRiepilogo] = useState(null);
  const [importoRiepilogo, setImportoRiepilogo] = useState("");
  const [savingImportoRiepilogo, setSavingImportoRiepilogo] = useState(false);

  const inserisciCambioRiga = useCallback((row) => {
    const dataCambio =
      row.dataCambio ||
      String(
        row.transactionCompleteDate ||
          row.transactionDepartDate ||
          row.orderDate ||
          "",
      ).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dataCambio)) {
      setErrore("Data transazione non disponibile per il tasso di cambio");
      return;
    }
    setRigaCambio({ ...row, dataCambio });
    setValoreCambio("");
  }, []);

  const salvaCambioRiga = useCallback(async () => {
    if (!rigaCambio) return;
    setSavingCambio(true);
    setErrore("");
    try {
      await axios.post(
        apiUrl + "/contab/controlli-corrispettivi/cambi",
        {
          cambi: [
            {
              data: rigaCambio.dataCambio,
              valuta: rigaCambio.valutaEstera,
              tassoCambio: valoreCambio,
            },
          ],
        },
        { headers: { Authorization: "Bearer " + token } },
      );
      setRigaCambio(null);
      if (!fileId) {
        const response = await axios.post(
          apiUrl + "/contab/get/controlloCorrispettiviAmazon",
          { fileName, rows },
          { headers: { Authorization: "Bearer " + token } },
        );
        setRows(response.data.data || []);
        setSummary(response.data.summary || null);
      } else setRefreshVersion((previous) => previous + 1);
    } catch (error) {
      setErrore(
        error.response?.data?.error ||
          error.message ||
          "Errore salvataggio tasso di cambio",
      );
    } finally {
      setSavingCambio(false);
    }
  }, [fileId, fileName, rigaCambio, rows, token, valoreCambio]);

  const apriConfermaImportoDifferente = useCallback((row) => {
    setRigaImportoRiepilogo(row);
    setImportoRiepilogo(String(row.importoDomino ?? row.amount ?? ""));
  }, []);

  const confermaConImportoDifferente = useCallback(async () => {
    if (!rigaImportoRiepilogo) return;
    const abbinamenti = buildAbbinamentiConferma(rigaImportoRiepilogo);
    setSavingImportoRiepilogo(true);
    try {
      await axios.patch(apiUrl + "/contab/controlli-corrispettivi/righe/" + rigaImportoRiepilogo.idRigaControllo + "/conferma", { abbinamenti, importoRiepilogo }, { headers: { Authorization: "Bearer " + token } });
      setRows((previousRows) => previousRows.map((row) => row.idRigaControllo === rigaImportoRiepilogo.idRigaControllo ? { ...row, confermata: true, statoRegistrazione: "CONFERMATO", importoRiepilogo } : row));
      setRigaImportoRiepilogo(null);
    } catch (error) { setConfermaErrore(error.response?.data?.error || error.message || "Errore conferma importo differente"); }
    finally { setSavingImportoRiepilogo(false); }
  }, [importoRiepilogo, rigaImportoRiepilogo, token]);

  const confermaEGeneraRimborso = useCallback(
    async (row) => {
      if (!row.idRigaControllo) {
        setConfermaErrore("Salva prima il file di controllo");
        return;
      }
      if (
        !window.confirm(
          "La riga verra confermata e verra creato un rimborso sintetico nelle Transazioni aggiunte. Resi_rimborsi e la spedizione Domino non verranno aggiornati. Continuare?",
        )
      )
        return;
      const abbinamenti = buildAbbinamentiConferma(row);
      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/conferma",
          { abbinamenti },
          { headers: { Authorization: "Bearer " + token } },
        );
        await axios.post(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/rimborso-sintetico",
          {},
          { headers: { Authorization: "Bearer " + token } },
        );
        setRows((previousRows) =>
          previousRows.map((currentRow) =>
            currentRow.idRigaControllo === row.idRigaControllo
              ? {
                  ...currentRow,
                  confermata: true,
                  statoRegistrazione: "CONFERMATO",
                  numeroAbbinamenti: abbinamenti.length,
                }
              : currentRow,
          ),
        );
        setConfermaMessaggio(
          "Riga confermata e rimborso sintetico creato in Transazioni aggiunte",
        );
      } catch (error) {
        setConfermaErrore(
          error.response?.data?.error ||
            error.message ||
            "Errore generazione rimborso sintetico",
        );
      }
    },
    [token],
  );

  const confermaRigaControllo = useCallback(
    async (row) => {
      if (!token || !row.idRigaControllo) {
        return;
      }

      const abbinamenti = buildAbbinamentiConferma(row);

      setConfermaErrore("");
      setConfermaMessaggio("");
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));

      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/conferma",
          { abbinamenti },
          { headers: { Authorization: "Bearer " + token } },
        );

        setRows((previousRows) =>
          previousRows.map((currentRow) =>
            currentRow.idRigaControllo === row.idRigaControllo
              ? {
                  ...currentRow,
                  confermata: true,
                  statoRegistrazione: "CONFERMATO",
                  numeroAbbinamenti: abbinamenti.length,
                }
              : currentRow,
          ),
        );
        setSelectedRows((previousRows) =>
          previousRows.filter(
            (selectedRow) =>
              selectedRow.idRigaControllo !== row.idRigaControllo,
          ),
        );
        setToggleClearSelectedRows((previous) => !previous);
        setConfermaMessaggio(
          abbinamenti.length > 0
            ? "Riga confermata"
            : "Riga confermata senza abbinamenti Domino",
        );
      } catch (error) {
        setConfermaErrore(
          error.response?.data?.error ||
            error.message ||
            "Errore conferma riga controllo",
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    },
    [token],
  );

  const deconfermaRigaControllo = useCallback(
    async (row, statoRegistrazione = "IN_ATTESA_CONFERMA") => {
      if (!token || !row.idRigaControllo) {
        return;
      }

      setConfermaErrore("");
      setConfermaMessaggio("");
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));

      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/deconferma",
          { statoRegistrazione },
          { headers: { Authorization: "Bearer " + token } },
        );
        setRows((previousRows) =>
          previousRows.map((currentRow) =>
            currentRow.idRigaControllo === row.idRigaControllo
              ? {
                  ...currentRow,
                  confermata: false,
                  statoRegistrazione,
                  numeroAbbinamenti: 0,
                }
              : currentRow,
          ),
        );
        setSelectedRows((previousRows) =>
          previousRows.filter(
            (selectedRow) =>
              selectedRow.idRigaControllo !== row.idRigaControllo,
          ),
        );
        setToggleClearSelectedRows((previous) => !previous);
        setConfermaMessaggio(
          statoRegistrazione === "DA_IGNORARE"
            ? "Riga ignorata"
            : "Riga riportata in attesa conferma",
        );
      } catch (error) {
        setConfermaErrore(
          error.response?.data?.error ||
            error.message ||
            "Errore de-conferma riga controllo",
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    },
    [token],
  );

  const selectedConfirmableRows = useMemo(
    () => selectedRows.filter(isRigaConfermabile),
    [selectedRows],
  );

  const selectedConfirmedRows = useMemo(
    () =>
      selectedRows.filter((row) => getStatoRegistrazione(row) === "CONFERMATO"),
    [selectedRows],
  );

  const handleSelectedRowsChange = useCallback((state) => {
    setSelectedRows(state.selectedRows || []);
  }, []);

  const confermaRigheSelezionate = useCallback(async () => {
    if (!token) {
      return;
    }

    const righeDaConfermare = selectedRows.filter(isRigaConfermabile);
    if (righeDaConfermare.length < 1) {
      setConfermaMessaggio("");
      setConfermaErrore("Nessuna riga selezionata confermabile");
      return;
    }

    setBulkConfirming(true);
    setConfermaErrore("");
    setConfermaMessaggio("");

    const confermate = [];
    const errori = [];

    for (const row of righeDaConfermare) {
      const abbinamenti = buildAbbinamentiConferma(row);
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));

      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/conferma",
          { abbinamenti },
          { headers: { Authorization: "Bearer " + token } },
        );
        confermate.push({
          idRigaControllo: row.idRigaControllo,
          numeroAbbinamenti: abbinamenti.length,
        });
      } catch (error) {
        errori.push(
          row.transactionEventId +
            ": " +
            (error.response?.data?.error || error.message || "errore conferma"),
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    }

    if (confermate.length > 0) {
      const confermateById = new Map(
        confermate.map((row) => [row.idRigaControllo, row]),
      );
      setRows((previousRows) =>
        previousRows.map((currentRow) => {
          const rigaConfermata = confermateById.get(currentRow.idRigaControllo);
          return rigaConfermata
            ? {
                ...currentRow,
                confermata: true,
                statoRegistrazione: "CONFERMATO",
                numeroAbbinamenti: rigaConfermata.numeroAbbinamenti,
              }
            : currentRow;
        }),
      );
      setSelectedRows([]);
      setToggleClearSelectedRows((previous) => !previous);
    }

    if (errori.length > 0) {
      setConfermaErrore(
        "Confermate " +
          confermate.length +
          " righe. Errori " +
          errori.length +
          ": " +
          errori.slice(0, 3).join(" | "),
      );
    } else {
      setConfermaMessaggio(
        "Confermate " + confermate.length + " righe selezionate",
      );
    }

    setBulkConfirming(false);
  }, [selectedRows, token]);

  const ignoraRigheSelezionate = useCallback(async () => {
    if (!token) {
      return;
    }

    const righeDaIgnorare = selectedRows.filter(isRigaConfermabile);
    if (righeDaIgnorare.length < 1) {
      setConfermaMessaggio("");
      setConfermaErrore("Nessuna riga in attesa selezionata");
      return;
    }

    setBulkIgnoring(true);
    setConfermaErrore("");
    setConfermaMessaggio("");
    const ignorate = [];
    const errori = [];

    for (const row of righeDaIgnorare) {
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));
      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/deconferma",
          { statoRegistrazione: "DA_IGNORARE" },
          { headers: { Authorization: "Bearer " + token } },
        );
        ignorate.push(row.idRigaControllo);
      } catch (error) {
        errori.push(
          row.transactionEventId +
            ": " +
            (error.response?.data?.error ||
              error.message ||
              "errore ignoramento"),
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    }

    if (ignorate.length > 0) {
      const ignorateSet = new Set(ignorate);
      setRows((previousRows) =>
        previousRows.map((currentRow) =>
          ignorateSet.has(currentRow.idRigaControllo)
            ? {
                ...currentRow,
                confermata: false,
                statoRegistrazione: "DA_IGNORARE",
                numeroAbbinamenti: 0,
              }
            : currentRow,
        ),
      );
      setSelectedRows([]);
      setToggleClearSelectedRows((previous) => !previous);
    }

    if (errori.length > 0) {
      setConfermaErrore(
        "Ignorate " +
          ignorate.length +
          " righe. Errori " +
          errori.length +
          ": " +
          errori.slice(0, 3).join(" | "),
      );
    } else {
      setConfermaMessaggio(
        "Ignorate " + ignorate.length + " righe selezionate",
      );
    }

    setBulkIgnoring(false);
  }, [selectedRows, token]);

  const deconfermaRigheSelezionate = useCallback(async () => {
    if (!token) {
      return;
    }

    const righeDaDeconfermare = selectedRows.filter(
      (row) => getStatoRegistrazione(row) === "CONFERMATO",
    );
    if (righeDaDeconfermare.length < 1) {
      setConfermaMessaggio("");
      setConfermaErrore("Nessuna riga confermata selezionata");
      return;
    }

    setBulkUnconfirming(true);
    setConfermaErrore("");
    setConfermaMessaggio("");
    const deconfermate = [];
    const errori = [];
    const statoRegistrazione = "IN_ATTESA_CONFERMA";

    for (const row of righeDaDeconfermare) {
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));
      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/deconferma",
          { statoRegistrazione },
          { headers: { Authorization: "Bearer " + token } },
        );
        deconfermate.push(row.idRigaControllo);
      } catch (error) {
        errori.push(
          row.transactionEventId +
            ": " +
            (error.response?.data?.error ||
              error.message ||
              "errore de-conferma"),
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    }

    if (deconfermate.length > 0) {
      const deconfermateSet = new Set(deconfermate);
      setRows((previousRows) =>
        previousRows.map((currentRow) =>
          deconfermateSet.has(currentRow.idRigaControllo)
            ? {
                ...currentRow,
                confermata: false,
                statoRegistrazione,
                numeroAbbinamenti: 0,
              }
            : currentRow,
        ),
      );
      setSelectedRows([]);
      setToggleClearSelectedRows((previous) => !previous);
    }

    if (errori.length > 0) {
      setConfermaErrore(
        "De-confermate " +
          deconfermate.length +
          " righe. Errori " +
          errori.length +
          ": " +
          errori.slice(0, 3).join(" | "),
      );
    } else {
      setConfermaMessaggio(
        "De-confermate " + deconfermate.length + " righe selezionate",
      );
    }

    setBulkUnconfirming(false);
  }, [selectedRows, token]);

  const collegaTransazioneDomino = useCallback(
    (transazione) => {
      if (!rigaDaCollegare) {
        return;
      }
      const isRefund = rigaDaCollegare.transactionType === "REFUND";
      setRows((previousRows) =>
        previousRows.map((row) => {
          if (row.id !== rigaDaCollegare.id) {
            return row;
          }
          const controlliManuali = Array.from(
            new Set([
              ...(row.controlliManuali || []),
              "Abbinamento Domino selezionato manualmente",
            ]),
          );
          if (isRefund) {
            const reso = {
              ...transazione,
              idReso: transazione.idReso,
              totaleRimborso: transazione.importo,
            };
            return {
              ...row,
              resi: [reso],
              matchedResoId: transazione.idReso,
              importoDomino: transazione.importo,
              differenza:
                Math.abs(Number(row.amount || 0)) -
                Math.abs(Number(transazione.importo || 0)),
              statoControllo: "MANUALE",
              controlliManuali,
            };
          }
          const spedizione = {
            ...transazione,
            idSpedizioneDomino: transazione.idSpedizioneDomino,
            totaleImportoSpedizione: transazione.importo,
          };
          return {
            ...row,
            spedizioni: [spedizione],
            matchedShipmentIds: [transazione.idSpedizioneDomino],
            importoDomino: transazione.importo,
            differenza:
              Math.abs(Number(row.amount || 0)) -
              Math.abs(Number(transazione.importo || 0)),
            statoControllo: "MANUALE",
            controlliManuali,
          };
        }),
      );
      setRigaDaCollegare(null);
    },
    [rigaDaCollegare],
  );

  const columns = useMemo(
    () => [
      {
        name: "Esito",
        width: "60px",
        cell: (row) => <StatoBadge row={row} />,
      },
      {
        name: "Tipo",
        cell: (row) => <TipoTransazione row={row} />,
        selector: (row) => row.transactionType,
        sortable: true,
        width: "100px",
      },
      {
        name: "Rif Amazon",
        selector: (row) => row.transactionEventId,
        sortable: true,
        width: "175px",
      },
      {
        name: "Stato",
        width: "70px",
        cell: (row) => <StatoRegistrazioneBadge row={row} />,
      },
      {
        name: "Registrazione",
        width: "150px",
        cell: (row) => (
          <ConfermaCell
            row={row}
            isLoading={Boolean(righeInConferma[row.idRigaControllo])}
            onConferma={confermaRigaControllo}
            onDeconferma={deconfermaRigaControllo}
            onIgnora={(row) => deconfermaRigaControllo(row, "DA_IGNORARE")}
          />
        ),
      },
      {
        name: "Azioni",
        width: "145px",
        cell: (row) => {
          const valutaEstera = (row.currencyCodes || [row.currency]).some(
            (valuta) => valuta && String(valuta).toUpperCase() !== "EUR",
          );
          const mostraCambio = valutaEstera && !row.tassoCambio;
          const mostraCollega =
            !valutaEstera &&
            getStatoRegistrazione(row) === "IN_ATTESA_CONFERMA" &&
            !haAbbinamentoDominoCorretto(row);
          return (
            <div className="flex items-center">
              {getStatoRegistrazione(row) === "IN_ATTESA_CONFERMA" && row.idRigaControllo && (
                <button
                  className="tastoArancio br3 mr1 pointer"
                  type="button"
                  title="Conferma con importo differente"
                  aria-label="Conferma con importo differente"
                  onClick={() => apriConfermaImportoDifferente(row)}
                >
                  <span className="material-symbols-outlined">swap_vert</span>
                </button>
              )}
              {row.transactionType === "SALE" &&
                getStatoRegistrazione(row) === "IN_ATTESA_CONFERMA" && (
                  <button
                    className="tastoArancio br3 mr1 pointer"
                    type="button"
                    title="Conferma e genera rimborso: non crea Resi_rimborsi"
                    aria-label="Conferma e genera rimborso"
                    onClick={() => confermaEGeneraRimborso(row)}
                  >
                    <span className="material-symbols-outlined">sync_alt</span>
                  </button>
                )}
              {mostraCollega && (
                <button
                  className="dim tastoInputOrizzontale br3"
                  type="button"
                  title="Collega"
                  aria-label="Collega"
                  onClick={() => setRigaDaCollegare(row)}
                >
                  <span className="material-symbols-outlined">add_link</span>
                </button>
              )}
              {mostraCambio && (
                <button
                  className="dim tastoInputOrizzontale br3"
                  type="button"
                  title="Inserisci tasso di cambio"
                  aria-label="Inserisci tasso di cambio"
                  onClick={() => inserisciCambioRiga(row)}
                >
                  <span className="material-symbols-outlined">
                    currency_exchange
                  </span>
                </button>
              )}
              {!mostraCollega && !mostraCambio && getStatoRegistrazione(row) !== "IN_ATTESA_CONFERMA" && (
                <span className="colScrittaDef-20">-</span>
              )}
            </div>
          );
        },
      },
      {
        name: "Valuta",
        selector: (row) => row.currency,
        sortable: true,
        width: "95px",
      },
      {
        name: "Imp. report",
        selector: (row) => row.amount,
        sortable: true,
        width: "130px",
        right: true,
        cell: (row) => formatImporto(row.amount),
      },
      {
        name: "Imp. Domino",
        selector: (row) => row.importoDomino,
        sortable: true,
        width: "135px",
        right: true,
        cell: (row) => formatImporto(row.importoDomino),
      },
      {
        name: "Diff.",
        selector: (row) => row.differenza,
        sortable: true,
        width: "100px",
        right: true,
        conditionalCellStyles: [
          {
            when: (row) =>
              row.differenza !== null &&
              Math.abs(Number(row.differenza)) > 0.01,
            style: { backgroundColor: "var(--colAttesa)" },
          },
        ],
        cell: (row) => formatImporto(row.differenza),
      },
      {
        name: "Spedizioni",
        width: "280px",
        cell: (row) => <SpedizioniCell row={row} setOrdine={setOrdine} />,
      },
      {
        name: "Emissione scontrino",
        width: "190px",
        cell: (row) => (
          <ValoriSpedizioniCell row={row} fieldName="emissioneScontrino" />
        ),
      },
      {
        name: "Tipo spedizione",
        width: "220px",
        cell: (row) => (
          <ValoriSpedizioniCell row={row} fieldName="tipoSpedizione" />
        ),
      },
      {
        name: "Rimborsi",
        width: "220px",
        cell: (row) => <ResiCell row={row} />,
      },
      {
        name: "Reg. precedenti",
        width: "260px",
        cell: (row) => <ControlliPrecedentiCell row={row} />,
      },
      {
        name: "Date part/compl",
        width: "160px",
        cell: (row) => (
          <div>
            <div>{formatData(row.transactionDepartDate)}</div>
            <div>{formatData(row.transactionCompleteDate)}</div>
          </div>
        ),
      },
      {
        name: "Esito",
        width: "280px",
        cell: (row) => <ProblemiCell row={row} />,
      },
      {
        name: "Righe report",
        width: "400px",
        wrap: true,
        cell: (row) => <DettaglioRigheReportCell row={row} />,
      },
    ],
    [
      apriConfermaImportoDifferente,
      confermaEGeneraRimborso,
      confermaRigaControllo,
      deconfermaRigaControllo,
      inserisciCambioRiga,
      righeInConferma,
    ],
  );

  const filteredRows = useMemo(() => {
    const rifAmazon = filtroRifAmazon.trim().toLowerCase();

    return rows.filter((row) => {
      const matchRif =
        !rifAmazon ||
        String(row.transactionEventId || "")
          .toLowerCase()
          .includes(rifAmazon);
      const matchTipo = !filtroTipo || row.transactionType === filtroTipo;
      const matchEsito = !filtroEsito || row.statoControllo === filtroEsito;
      const matchConferma =
        !filtroConferma || getStatoRegistrazione(row) === filtroConferma;

      return matchRif && matchTipo && matchEsito && matchConferma;
    });
  }, [rows, filtroRifAmazon, filtroTipo, filtroEsito, filtroConferma]);

  const resetFiltri = () => {
    setFiltroRifAmazon("");
    setFiltroTipo("");
    setFiltroEsito("");
    setFiltroConferma("");
  };

  useEffect(() => {
    if (!fileId || !token) {
      return;
    }

    setLoading(true);
    setErrore("");
    axios
      .get(apiUrl + "/contab/controlli-corrispettivi/files/" + fileId, {
        headers: { Authorization: "Bearer " + token },
      })
      .then(async (response) => {
        const file = response.data.file || {};
        const righe = response.data.righe || [];
        const savedFileIdCorrente = file.id || fileId;
        const righeSalvateByProgressivo = new Map();
        const rowsDaRicontrollare = righe.map((riga) => {
          const raw = parseMaybeJson(riga.raw_json, {});
          const progressivo = Number(riga.progressivo_riga);
          righeSalvateByProgressivo.set(progressivo, riga);
          return {
            ...raw,
            sourceIndex: Number.isFinite(progressivo)
              ? progressivo
              : raw.sourceIndex,
            csvRowNumber: riga.numero_riga || raw.csvRowNumber,
            transactionEventId:
              riga.chiave_transazione || raw.transactionEventId,
            transactionType: riga.tipo_transazione || raw.transactionType,
            amount: riga.importo_report ?? raw.amount,
            currency: riga.valuta || raw.currency,
            transactionCompleteDate:
              raw.transactionCompleteDate || riga.data_transazione || "",
            transactionDepartDate:
              raw.transactionDepartDate ||
              riga.data_partenza ||
              riga.data_transazione ||
              "",
          };
        });

        const confrontoAggiornato = await axios.post(
          apiUrl + "/contab/get/controlloCorrispettiviAmazon",
          {
            fileName: file.nome_file_originale || "File " + fileId,
            rows: rowsDaRicontrollare,
            excludeFileId: savedFileIdCorrente,
          },
          { headers: { Authorization: "Bearer " + token } },
        );
        const nextRows = (confrontoAggiornato.data.data || []).map((row) => {
          const rigaSalvata = righeSalvateByProgressivo.get(
            Number(row.sourceIndex),
          );
          if (!rigaSalvata) {
            return row;
          }

          return {
            ...row,
            idRigaControllo: rigaSalvata.id,
            idFileControllo: rigaSalvata.id_file,
            statoRegistrazione:
              rigaSalvata.stato_registrazione ||
              (rigaSalvata.confermata ? "CONFERMATO" : "IN_ATTESA_CONFERMA"),
            confermata: Boolean(rigaSalvata.confermata),
            numeroAbbinamenti: Number(rigaSalvata.numero_abbinamenti || 0),
          };
        });

        setFileName(file.nome_file_originale || "File " + fileId);
        setFileHash(file.hash_file || null);
        setAccountControllo(file.account || "");
        setPeriodoDa(formatData(file.periodo_da));
        setPeriodoA(formatData(file.periodo_a));
        setRows(nextRows);
        setSelectedRows([]);
        setToggleClearSelectedRows((previous) => !previous);
        setSummary(
          confrontoAggiornato.data.summary || {
            totaleRighe: file.righe_totali || nextRows.length,
            righeCsvOrigine: file.righe_totali || nextRows.length,
            righeOk: file.righe_ok || 0,
            righeManuali: file.righe_manuali || 0,
            righeKo: file.righe_ko || 0,
            sale: nextRows.filter((row) => row.transactionType === "SALE")
              .length,
            refund: nextRows.filter((row) => row.transactionType === "REFUND")
              .length,
          },
        );
        setSavedFileId(savedFileIdCorrente);
        resetFiltri();
      })
      .catch((error) => {
        setErrore(
          error.response?.data?.error ||
            error.message ||
            "Errore caricamento file controllo",
        );
      })
      .finally(() => setLoading(false));
  }, [fileId, refreshVersion, token]);

  const salvaFileControllo = async () => {
    if (!token || !fileName || rows.length < 1) {
      return;
    }

    setSavingFile(true);
    setSalvataggioErrore("");
    try {
      const response = await axios.post(
        apiUrl + "/contab/controlli-corrispettivi/files",
        {
          fileName,
          piattaforma: "AMAZON",
          tipoReport: "REPORT_TRANSAZIONI_IVA",
          hashFile: fileHash,
          account: accountControllo || null,
          periodoDa: periodoDa || null,
          periodoA: periodoA || null,
          rows,
          summary,
          metadata: {
            source: "controlloCorrispettiviAmazon",
          },
        },
        { headers: { Authorization: "Bearer " + token } },
      );
      const nextSavedFileId = response.data.id;
      const righeSalvate = response.data.righe || [];
      const righeByProgressivo = new Map(
        righeSalvate.map((riga) => [Number(riga.progressivo_riga), riga]),
      );

      setSavedFileId(nextSavedFileId);
      setRows((previousRows) =>
        previousRows.map((row) => {
          const rigaSalvata = righeByProgressivo.get(Number(row.sourceIndex));
          if (!rigaSalvata) {
            return row;
          }

          return {
            ...row,
            idFileControllo: nextSavedFileId,
            idRigaControllo: rigaSalvata.id,
            confermata: false,
            numeroAbbinamenti: 0,
          };
        }),
      );
    } catch (error) {
      setSalvataggioErrore(
        error.response?.data?.error ||
          error.message ||
          "Errore salvataggio file controllo",
      );
    } finally {
      setSavingFile(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    setErrore("");
    setSalvataggioErrore("");
    setConfermaErrore("");
    setConfermaMessaggio("");
    setSelectedRows([]);
    setToggleClearSelectedRows((previous) => !previous);
    setSavedFileId(null);
    setFileHash(null);
    setAccountControllo("");
    setPeriodoDa("");
    setPeriodoA("");
    setLoading(true);
    setRows([]);
    setSummary(null);
    resetFiltri();
    setFileName(file.name);

    try {
      if (!token) {
        throw new Error("Token non ancora disponibile");
      }
      const hash = await calcolaHashFile(file);
      setFileHash(hash);
      const text = await file.text();
      const parsedRows = parseAmazonCsv(text);
      const periodo = getPeriodoDaRows(parsedRows);
      setPeriodoDa(periodo.periodoDa);
      setPeriodoA(periodo.periodoA);
      const response = await axios.post(
        `${apiUrl}/contab/get/controlloCorrispettiviAmazon`,
        { fileName: file.name, rows: parsedRows },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows(response.data.data || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      setErrore(
        error.response?.data?.error || error.message || "Errore nel controllo",
      );
    } finally {
      setLoading(false);
    }
  };

  if (ordine !== "NO DATA") {
    return (
      <ModificaOrdini
        utente={utente}
        ordine={ordine}
        setOrdineFunction={setOrdine}
        datiTendineOrdine={datiTendineOrdine}
      />
    );
  }

  return (
    <div className="sfondoBiancoTema pa3">
      <div className="flex flex-wrap items-center justify-between bb bordoColAlt pb3 mb3">
        <div>
          <div className="f4 b">Controllo corrispettivi Amazon</div>
          <div className="f6 colScrittaDef-20">
            Report Transazioni con IVA di Amazon
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end">
          <Link
            className={stilePulsanti.buttonClassesWhite + " mr2"}
            to="/controlliCorrispettivi"
          >
            Indietro
          </Link>
          <label
            className={stilePulsanti.buttonClassesWhite + " flex items-center"}
          >
            {stilePulsanti.tastoCaricaAspetto}
            <span className="ml2">Carica CSV</span>
            <input
              className="dn"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
            />
          </label>
        </div>
      </div>

      {fileName && (
        <div className="mb3">
          <span className="b">{fileName}</span>
          {summary && (
            <span className="ml3">
              Gruppi {summary.totaleRighe} - righe CSV {summary.righeCsvOrigine}{" "}
              - OK {summary.righeOk} - Manuali {summary.righeManuali} - KO{" "}
              {summary.righeKo} - SALE {summary.sale} - REFUND {summary.refund}
            </span>
          )}
        </div>
      )}

      {fileName && rows.length > 0 && (
        <div className="formRicercaOrizzontale pa2 flex flex-wrap items-center mb3">
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="text"
              placeholder="Account"
              value={accountControllo}
              onChange={(event) => setAccountControllo(event.target.value)}
              disabled={Boolean(savedFileId)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="date"
              value={periodoDa}
              onChange={(event) => setPeriodoDa(event.target.value)}
              disabled={Boolean(savedFileId)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="date"
              value={periodoA}
              onChange={(event) => setPeriodoA(event.target.value)}
              disabled={Boolean(savedFileId)}
            />
          </div>
        </div>
      )}

      {fileName && rows.length > 0 && (
        <div className="mb3 flex flex-wrap items-center">
          {!savedFileId && (
            <button
              className={stilePulsanti.buttonClassesWhite}
              type="button"
              onClick={salvaFileControllo}
              disabled={savingFile}
            >
              {savingFile ? "Salvo..." : "Salva controllo"}
            </button>
          )}
          {savedFileId && (
            <span className="tastoVerde white br2 ph2 pv1">
              File controllo {savedFileId}
            </span>
          )}
        </div>
      )}

      {salvataggioErrore && (
        <div className="tastoRosso white pa3 br2 mb3">{salvataggioErrore}</div>
      )}

      {confermaErrore && (
        <div className="tastoRosso white pa3 br2 mb3">{confermaErrore}</div>
      )}

      <div className="formRicercaOrizzontale pa2 flex flex-wrap justify-between items-center mb3">
        <div className="flex flex-wrap items-center">
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="text"
              placeholder="Rif Amazon"
              value={filtroRifAmazon}
              onChange={(event) => setFiltroRifAmazon(event.target.value)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroTipo}
              onChange={(event) => setFiltroTipo(event.target.value)}
            >
              <option value="">Tipo: tutti</option>
              <option value="SALE">VENDITA</option>
              <option value="REFUND">RIMBORSO</option>
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroEsito}
              onChange={(event) => setFiltroEsito(event.target.value)}
            >
              <option value="">Esito: tutti</option>
              <option value="OK">OK</option>
              <option value="KO">KO</option>
              <option value="MANUALE">MANUALE</option>
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroConferma}
              onChange={(event) => setFiltroConferma(event.target.value)}
            >
              <option value="">Registrazione: tutte</option>
              <option value="IN_ATTESA_CONFERMA">In attesa conferma</option>
              <option value="CONFERMATO">Confermato</option>
              <option value="DA_IGNORARE">Da ignorare</option>
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <button
              className="dim tastoInputOrizzontale br3"
              type="button"
              onClick={resetFiltri}
            >
              RESET
            </button>
          </div>
        </div>
        <div className="colScrittaDef-20 mb2">
          Righe {filteredRows.length}/{rows.length}
        </div>
      </div>

      {errore && <div className="tastoRosso white pa3 br2 mb3">{errore}</div>}
      {confermaMessaggio && (
        <div className="tastoVerde white pa3 br2 mb3">{confermaMessaggio}</div>
      )}

      {savedFileId && filteredRows.length > 0 && (
        <div className="formRicercaOrizzontale pa2 flex flex-wrap justify-between items-center mb3">
          <div className="colScrittaDef-20 mb2">
            Selezionate {selectedRows.length} - confermabili{" "}
            {selectedConfirmableRows.length} - confermate{" "}
            {selectedConfirmedRows.length}
          </div>
          <div className="flex flex-wrap justify-end items-center mb2">
            <button
              className="tastoVerde white br2 ph2 pv1 mr2 flex justify-center items-center"
              type="button"
              title="Conferma selezionate"
              onClick={confermaRigheSelezionate}
              disabled={bulkConfirming || selectedConfirmableRows.length < 1}
            >
              <span className="material-symbols-outlined mr1">thumb_up</span>
              {bulkConfirming ? "Confermo..." : "Conferma selezionate"}
            </button>
            <button
              className="sfondoColScuro white br2 ph2 pv1 mr2 flex justify-center items-center"
              type="button"
              title="Ignora selezionate"
              onClick={ignoraRigheSelezionate}
              disabled={bulkIgnoring || selectedConfirmableRows.length < 1}
            >
              <span className="material-symbols-outlined mr1">thumb_down</span>
              {bulkIgnoring ? "Ignoro..." : "Ignora selezionate"}
            </button>
            <button
              className="tastoRosso white br2 ph2 pv1 flex justify-center items-center"
              type="button"
              title="De-conferma selezionate"
              onClick={deconfermaRigheSelezionate}
              disabled={bulkUnconfirming || selectedConfirmedRows.length < 1}
            >
              <span className="material-symbols-outlined mr1">undo</span>
              {bulkUnconfirming ? "De-confermo..." : "De-conferma selezionate"}
            </button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={filteredRows}
        progressPending={loading}
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 100, 200]}
        dense
        highlightOnHover
        selectableRows
        selectableRowsVisibleOnly
        selectableRowDisabled={(row) => !isRigaSelezionabile(row)}
        onSelectedRowsChange={handleSelectedRowsChange}
        clearSelectedRows={toggleClearSelectedRows}
      />

      {rigaDaCollegare && (
        <ModalRicercaTransazioneDomino
          token={token}
          tipoMovimento={
            rigaDaCollegare.transactionType === "REFUND"
              ? "RIMBORSO"
              : "SPEDIZIONE"
          }
          onSelect={collegaTransazioneDomino}
          onClose={() => setRigaDaCollegare(null)}
        />
      )}
      {rigaCambio && (
        <ModalCambioValuta
          riga={rigaCambio}
          valore={valoreCambio}
          onChange={setValoreCambio}
          onClose={() => !savingCambio && setRigaCambio(null)}
          onSave={salvaCambioRiga}
          saving={savingCambio}
        />
      )}
      {rigaImportoRiepilogo && (
        <ModalImportoRiepilogo
          valore={importoRiepilogo}
          onChange={setImportoRiepilogo}
          onClose={() => !savingImportoRiepilogo && setRigaImportoRiepilogo(null)}
          onSave={confermaConImportoDifferente}
          saving={savingImportoRiepilogo}
        />
      )}
    </div>
  );
}

export default withLogisticaConfiguration(ControlloCorrispettiviAmazon);
